// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getCssUnitField from "@store/Framework/Common/Fields/Toolkit/cssUnit";
import getSpacingPresetField, { spacingPresetOptions } from "@store/Framework/Common/Fields/Toolkit/spacingPreset";
import { defaultBorderProps } from "@store/Library/Components/Toolkit/Block/defaultProps";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import React, { FC, useMemo } from "react";
import { css, useTheme } from "styled-components";

const enum fields {
    presetSpacing = "presetSpacing",
    customSpacing = "customSpacing",
    showHorizontalRule = "showHorizontalRule",
}

/* Starter Custom Code - Start */
const presetSpacingField = getSpacingPresetField(fields.presetSpacing, { showCustom: true });
const customSpacingField = getCssUnitField(fields.customSpacing);
/* Starter Custom Code - End */

interface Props extends WidgetProps {
    fields: {
        [fields.presetSpacing]: string;
        [fields.customSpacing]: string;
        [fields.showHorizontalRule]: boolean;
    };
}

export interface SpacerStyles {
    horizontalRule?: InjectableCss;
}

export const spacerStyles: SpacerStyles = {
    horizontalRule: {
        css: css`
            margin: 0;
            border: 0;
            border-top: ${defaultBorderProps.width} ${defaultBorderProps.type} ${getColor(defaultBorderProps.color)};
        `,
    },
};

const Spacer: FC<Props> = ({ fields: { presetSpacing, customSpacing, showHorizontalRule } }) => {
    const theme = useTheme();

    const spacing = useMemo(() => {
        return presetSpacingField.parse(presetSpacing, theme) || customSpacingField.parse(customSpacing);
    }, [theme, presetSpacing, customSpacing]);

    const styles = useMemo(() => {
        const _spacing = showHorizontalRule ? spacing : spacing?.map(x => (x ? `calc(${x} / 2)` : null));

        return mergeProps<SpacerStyles>(spacerStyles, {
            horizontalRule: {
                css: css`
                    ${!showHorizontalRule ? "border: 0;" : null}
                    ${breakpointValues(_spacing, ["margin-top", "margin-bottom"])}
                `,
            },
        });
    }, [spacerStyles, spacing, showHorizontalRule]);

    return <StyledWrapper as="hr" aria-hidden="true" {...styles.horizontalRule} />;
};

const widgetModule: WidgetModule = {
    component: Spacer,
    definition: {
        group: "Basic",
        displayName: "Spacer",
        icon: "arrows-from-line",
        fieldDefinitions: [
            {
                ...presetSpacingField.definition,
                fieldType: "Contextual",
            },
            {
                ...customSpacingField.definition,
                fieldType: "Contextual",
                isVisible: x => x.fields[fields.presetSpacing] === spacingPresetOptions.custom.id,
            },
            {
                name: fields.showHorizontalRule,
                displayName: "Show Horizontal Rule",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
            },
        ],
    },
};

export default widgetModule;
